import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import clsx from 'clsx'

import { emailDelete } from '@/store/email'
import { usersUpdateUserNotAdmin } from '@/store/users';

import { tooltipTitles } from "@/constants/tooltipTitles"
import { copyToClipboard } from "@/utils/app"

import AppCard from "@/components/AppCard";
import AppText from "@/components/AppText";
import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import AppSwitch from "@/components/AppSwitch";
import AppIcon from "@/components/AppIcon";
import AppTooltip from "@/components/AppTooltip";
import DynamicInputList from "@/components/DynamicInputList";
import AppInfoSection from "@/components/AppInfoSection";
import AppCheckbox from "../AppCheckbox";

interface UserConfigProps {
  config: any;
  onChange: (field: string, value: any) => void;
  isSaved?: boolean;
}

const leadTypeOptions = [
  { label: "Message Lead w phone", value: "message_with_phone" },
  { label: "Message Lead w/o phone", value: "message_without_phone" },
  { label: "Missed Call Lead", value: "missed_call" },
  { label: "Answered Call Lead", value: "answered_call" },
];

const UserConfig: React.FC<UserConfigProps> = ({ config, onChange, isSaved }) => {
  const dispatch = useAppDispatch();

  const { isMobile } = useAppSelector((state) => state.app);
  const { me } = useAppSelector((state: any) => state.auth);

  const [alertEmails, setAlertEmails] = useState(
    config.emails.map((email: any) => ({ id: email.id, value: email.email }))
  );
  const [initialEmails, setInitialEmails] = useState(
    config.initial_message_emails.map((email: any) => ({ value: email }))
  );
  const [alertPhones, setAlertPhones] = useState(
    config.phones.map((phone: any) => ({ id: phone.id, value: phone.phone_number }))
  );
  const [leadAutoEmailTypes, setLeadAutoEmailTypes] = useState(
    config.lead_auto_email_types || []
  );
  const [dailyRecapEmails, setDailyRecapEmails] = useState(
    config.daily_recap_emails ? config.daily_recap_emails.map((email: any) => ({ value: email })) : []
  );

  const [isEdit, setIsEdit] = useState(false)

  const agencyUserPassword = me.agency?.name?.slice(0, 2).toUpperCase() + "Z9lcNi"

  useEffect(() => {
    if (isSaved) {
      setIsEdit(false)
    }
  }, [isSaved])

  useEffect(() => {
    setLeadAutoEmailTypes(config.lead_auto_email_types || []);
    setDailyRecapEmails(config.daily_recap_emails ? config.daily_recap_emails.map((email: any) => ({ value: email })) : []);
  }, [config.lead_auto_email_types, config.daily_recap_emails]);

  const handleChangeEmails = (updatedEmails: { id?: number; value: string }[]) => {
    setAlertEmails(updatedEmails);
    const updatedConfigEmails = updatedEmails.map((email) => ({
      id: email.id,
      email: email.value,
    }));
    onChange("emails", updatedConfigEmails);
  };

  const handleChangeInitialEmails = (updatedEmails: { id?: number; value: string }[]) => {
    setInitialEmails(updatedEmails);
    const updatedConfigInitialEmails = updatedEmails.map((email) => email.value);
    onChange("initial_message_emails", updatedConfigInitialEmails);
  };
  
  const handleDeleteEmail = async (id: number | undefined) => {
    if (id) {
      await dispatch(emailDelete(id)); // Optional: API call
    }
    setAlertEmails((prev: any) => prev.filter((email: any) => email.id !== id));
    onChange(
      "emails",
      alertEmails.filter((email: any) => email.id !== id).map((email: any) => ({
        id: email.id,
        email: email.value,
      }))
    );
  };

  const handleDeleteInitialEmail = async (id: number | undefined) => {
    setInitialEmails((prev: any) => prev.filter((email: any) => email.id !== id));
    onChange(
      "initial_message_emails",
      initialEmails.filter((email: any) => email.id !== id).map((email: any) => email.value)
    );
  };

  // Handle phone updates
  const handleChangePhones = (updatedPhones: { id?: number; value: string }[]) => {
    setAlertPhones(updatedPhones);
    const updatedConfigPhones = updatedPhones.map((phone) => ({
      id: phone.id,
      phone: phone.value,
    }));
    onChange("phones", updatedConfigPhones);
  };

  const handleDeletePhone = async (id: number | undefined) => {
    setAlertPhones((prev: any) => prev.filter((phone: any) => phone.id !== id));
    onChange(
      "phones",
      alertPhones.filter((phone: any) => phone.id !== id).map((phone: any) => ({
        id: phone.id,
        phone: phone.value,
      }))
    );
  };

  const handleToggleEdit = () => {
    setIsEdit((prev) => !prev);
  }

  const handleCancelEdit = () => {
    // return back old value on cancel
    handleToggleEdit()
  }

  const handleLeadTypeChange = (type: string, checked: boolean) => {
    let updated = checked
      ? [...leadAutoEmailTypes, type]
      : leadAutoEmailTypes.filter((t: string) => t !== type);
    setLeadAutoEmailTypes(updated);
    onChange("lead_auto_email_types", updated);
  };

  const handleToggleAutoMarkLeads = async (value: boolean) => {
    // Update local/temp config
    handleChangeUserConfig('auto_mark_leads', value);
    // Persist immediately
    await dispatch(usersUpdateUserNotAdmin({ id: me.id, data: { auto_mark_leads: value } }));
  };

  const infoList = [
    {
      label: "Company Name",
      labelTooltip: tooltipTitles.companyName,
      content: (
        <AppInput
          value={config.company}
          onChange={(value) => handleChangeUserConfig('company', value)}
          placeholder="e.g. John Doe"
          fullWidth
          inputClassName={clsx(
            "text-center md:text-left",
            {
              "text-gray-700": !isEdit,
              "text-gray-900": isEdit,
            }
          )}
          className={clsx(
            "group transition-all duration-300 border",
            {
              "border-transparent focus:ring-0 bg-transparent text-gray-700": !isEdit,
              "border focus:ring-primary bg-white text-gray-900": isEdit,
            }
          )}
          disabled={!isEdit}
          endAdornment={
            isEdit
              ? <AppTooltip title="Cancel Editing">
                  <AppIcon
                    name="x"
                    className="text-error cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents input focus
                      handleCancelEdit();
                    }}
                  />
                </AppTooltip>
              : <AppTooltip title="Edit Company Name">
                  <AppIcon
                    name="edit"
                    className={clsx(
                      "opacity-0 cursor-pointer",
                      {
                        "group-hover:opacity-100": !isEdit,
                        "opacity-100": isMobile
                      }
                    )}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevents input focus
                      handleToggleEdit();
                    }}
                  />
                </AppTooltip>
          }
        />
      ),
    },
    {
      label: "LSA Customer ID",
      labelTooltip: tooltipTitles.customerId,
      content: (
        <AppInput
          value={config.customer_id}
          onChange={(value: any) => handleChangeUserConfig('customer_id', value)}
          placeholder="e.g. 123-456-7890"
          disabled
          className="border-0"
          inputClassName={clsx(
            "text-center md:text-left",
            {
              "text-gray-700": !isEdit,
              "text-gray-900": isEdit,
            }
          )}
        />
      ),
    },
    {
      label: "Login Details",
      labelTooltip: tooltipTitles.loginDetails,
      content: me.agency && !me.is_agency_admin ? (
        <AppBox column fullWidth justify="between" gap={2}>
          <AppInput
            label="Login Email"
            value={config.email}
            onChange={() => {}}
            placeholder="e.g. 123-456-7890"
            disabled
            inputClassName="text-center md:text-left text-gray-900"
            endAdornment={
              <AppTooltip title="Copy">
                <AppIcon
                  name="copy"
                  className="cursor-pointer"
                  onClick={() => copyToClipboard(config.email)}
                />
              </AppTooltip>
            }
          />
          <AppInput
            label="Login Password"
            value={agencyUserPassword}
            onChange={(value: any) => handleChangeUserConfig('customer_id', value)}
            placeholder="e.g. 123-456-7890"
            disabled
            inputClassName="text-center md:text-left text-gray-900"
            endAdornment={
              <AppTooltip title="Copy">
                <AppIcon
                  name="copy"
                  className="cursor-pointer"
                  onClick={() => copyToClipboard(agencyUserPassword)}
                />
              </AppTooltip>
            }
          />
        </AppBox>
      ) : null,
    },
    {
      label: "Auto Mark Leads",
      labelTooltip: tooltipTitles.autoMarkLeads,
      content: (
        <AppSwitch
          onChange={handleToggleAutoMarkLeads}
          label={`${config.auto_mark_leads ? "ON" : "OFF"}`}
          checked={config.auto_mark_leads}
          className="w-full justify-center md:justify-start"
        />
      ),
    },
    {
      label: "Emails for Alerts",
      labelTooltip: tooltipTitles.alertEmails,
      content: (
        <AppBox column fullWidth justify="between" gap={2}>
          <DynamicInputList
            labelPrefix="Alert Email"
            maxInputs={4}
            initialValues={alertEmails}
            onInputChange={handleChangeEmails}
            onDelete={handleDeleteEmail}
          />
        </AppBox>
      ),
    },
    {
      label: "1st Lead Auto-Email to User",
      labelTooltip: tooltipTitles.initialMessageEmail,
      // content: me.agency && me.agency.id === 14 && !me.is_agency_admin ? (
      content: me?.id === 289 || me?.lead_auto_email ? (
        <AppBox column fullWidth justify="between" gap={2}>
          <AppBox column fullWidth justify="between" gap={2}>
            {leadTypeOptions.map(opt => (
              <AppCheckbox
                key={opt.value}
                label={opt.label}
                checked={leadAutoEmailTypes.includes(opt.value)}
                onChange={(e: React.ChangeEvent<HTMLInputElement> | boolean) => {
                  let checked: boolean;
                  if (typeof e === 'boolean') {
                    checked = e;
                  } else {
                    checked = e.target.checked;
                  }
                  handleLeadTypeChange(opt.value, checked);
                }}
              />
            ))}
          </AppBox>
          <DynamicInputList
            labelPrefix="Your Email"
            maxInputs={4}
            initialValues={initialEmails}
            onInputChange={handleChangeInitialEmails}
            onDelete={handleDeleteInitialEmail}
          />
        </AppBox>
      ) : null,
    },
    {
      label: "Phones for Alerts",
      labelTooltip: tooltipTitles.alertCell,
      content: (
        <AppBox column fullWidth justify="between" gap={2}>
          <DynamicInputList
            labelPrefix="Alert Phone"
            maxInputs={2}
            initialValues={alertPhones}
            onInputChange={handleChangePhones}
            onDelete={handleDeletePhone}
          />
        </AppBox> 
      ),
    },
    // Daily Recap Emails section moved to AnsweredCallReplies
  ];

  const handleChangeUserConfig = (field: string, value: any) => {
    onChange(field, value)
  }

  return (
    <AppCard className="overflow-auto">
      <AppText variant="h5" className="border-b border-b-gray-200 pb-2">User Settings</AppText>
      
      <dl className="divide-y divide-gray-100">
        {infoList.map((item, index) => (
          item.content
            ? <AppInfoSection key={index} label={item.label} labelTootltip={item.labelTooltip} content={item.content} />
            : null
        ))}
      </dl>
    </AppCard>
  );
};

export default UserConfig